import { debounce } from "lodash";
import React from "react";
import { useDispatch } from "react-redux";
import { trackEvent } from "src/Amplitude";
import {
  resetBuyInput,
  resetSellInput,
  setBuyInput,
  setKnownSide,
  setSellInput,
  useSwapSlice,
} from "src/store/features/swap.slice";

/**
 * Custom hook for managing trade inputs.
 * Manages sellInput and buyInput states,
 * input change handlers, swapping inputs,
 * resetting inputs, and initializing default values.
 *
 * @returns Object containing state and functions for managing trade inputs.
 */
const useTradeInputs = () => {
  const { sellInput, buyInput } = useSwapSlice();
  const dispatch = useDispatch();

  /**
   * Updates sellInput value on input change.
   *
   * @param event - The input change event.
   */

  const handleSellInputChangeDebounce = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    dispatch(setKnownSide("sell"));
    dispatch(setSellInput({ value, units: value }));
    trackEvent("User Changed Sell Input", { sellInput }, "Trade");
  };
  const handleSellInputChange = debounce(handleSellInputChangeDebounce, 800); // 800ms debounce delay

  /**
   * Updates buyInput value on input change.
   *
   * @param event - The input change event.
   */
  const handleBuyInputChangeDebounce = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    dispatch(setKnownSide("buy"));
    dispatch(setBuyInput({ value, units: value }));
    trackEvent("User Changed Buy Input", { buyInput }, "Trade");
  };
  const handleBuyInputChange = debounce(handleBuyInputChangeDebounce, 800); // 800ms debounce delay

  React.useEffect(() => {
    // Cleanup the debounced functions when the component unmounts
    return () => {
      handleSellInputChange.cancel();
      handleBuyInputChange.cancel();
    };
  }, [handleSellInputChange, handleBuyInputChange]);

  /**
   * Resets sellInput and buyInput to their default values.
   */
  const resetInputs = (): void => {
    dispatch(resetSellInput());
    dispatch(resetBuyInput());
  };

  return {
    sellInput,
    handleSellInputChange,
    buyInput,
    handleBuyInputChange,
    resetInputs,
  };
};

export default useTradeInputs;
